import { useQueryClient } from '@tanstack/react-query';
import { RefreshCcwDot } from 'lucide-react';
import { useActiveStaleMarks } from '@/hooks/websocket/useActiveStaleMarks';
import { useStaleViewStore } from '@/hooks/websocket/useStaleViewStore';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';

/**
 * The taking end of `useStaleViewStore`: pulses in the toolbar while a live broadcast has
 * the view out of date, and reloads the query keys those broadcasts marked. Renders
 * nothing while the view is current.
 *
 * Shown on the marks this view is observing, acts on all of them - see the click handler.
 */
export const RefreshViewButton = () => {
  const queryClient = useQueryClient();
  const viewMarks = useActiveStaleMarks();
  const clearStale = useStaleViewStore((state) => state.clearStale);
  const allMarks = useStaleViewStore((state) => state.staleMarks);
  const t = useTranslations('breadcrumb');

  if (viewMarks.length === 0) {
    return null;
  }

  return (
    <Button
      variant="main-toolbar"
      size="main-toolbar"
      data-cy="refresh-view-button"
      tooltip={t('reloadView')}
      icon={RefreshCcwDot}
      iconClassName="icon-6 animate-pulse stroke-highlight-base! hover:stroke-highlight-hover-color!"
      onClick={() => {
        // Cleared first: `refetchType: 'all'` starts fetches, and a marking that lands
        // mid-fetch must be free to raise the button again.
        //
        // Every mark, not just the ones this view shows: restoring from the bin also puts a
        // row back in its folder, and invalidating that listing while it is unmounted still
        // makes it refetch when the viewer walks back to it.
        clearStale();
        allMarks.forEach(({ queryKey }) => {
          void queryClient.invalidateQueries({ queryKey, refetchType: 'all' });
        });
      }}
    />
  );
};
